import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_COLORS, EMAIL_EVENT_LABELS, emailLabel, emailSourceLabel, eventToStatus } from './emailLogLabels'


export default /*#__PURE__*/_defineComponent({
  __name: 'EmailThreadDrawer',
  props: {
  show: { type: Boolean, default: false },
  recipientId: { type: Number, default: null },
  recipient: { type: Object, default: null }
},
  emits: ["close"],
  setup(__props, { emit }: { emit: ({ (e: 'close'): void }), expose: any, slots: any, attrs: any }) {

const props = __props

// One customer's emails as a list, newest first, each with its status and an optional
// event history (get-email-log-for-group?recipient_id= and ?message_id=). Read only:
// sending an email needs a transaction, so each email links to its transaction, where
// the Send email drawer lives.


const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/email-log`

const loading = ref(false)
const emails = ref<any[]>([])
const openHistory = ref<number | null>(null)
const history = ref<any[]>([])
const historyLoading = ref(false)

const first = computed(() => (emails.value.length ? emails.value[0] : props.recipient))
const recipientName = computed(() => (first.value ? [first.value.recipient_first_name, first.value.recipient_last_name].filter(Boolean).join(' ') : ''))
const recipientEmail = computed(() => (first.value ? first.value.recipient_email : ''))

function statusLabel(eventType: string) { return EMAIL_STATUS_LABELS[eventToStatus(eventType)] }
function statusColor(eventType: string) { return EMAIL_STATUS_COLORS[eventToStatus(eventType)] }
function eventLabel(h: any) {
  const base = EMAIL_EVENT_LABELS[h.event_type] || h.event_type
  return h.event_type === 'Bounce' && h.bounce_type ? base + ' (' + String(h.bounce_type).toLowerCase() + ')' : base
}
function formatTime(value: string) {
  try { return formatGroupTime(value) } catch (e) { return '' }
}
// Short date (no weekday) so the row's left column keeps room on a phone
function formatWhen(value: string) {
  try { return formatGroupDate(value, { dayOfWeek: false }) + ' ' + formatGroupTime(value) } catch (e) { return String(value).slice(0, 16) }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function load() {
  if (!props.recipientId) return
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}?recipient_id=${props.recipientId}&count=200`, config)
    emails.value = (response.data.events || []).slice().reverse()
  } catch (e) {
    proxy.$notify({ type: 'danger', message: 'Could not load this customer\'s emails. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

async function toggleHistory(m: any) {
  if (openHistory.value === m.id) {
    openHistory.value = null
    return
  }
  openHistory.value = m.id
  history.value = []
  historyLoading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}?message_id=${encodeURIComponent(m.message_id)}`, config)
    history.value = response.data.history || []
  } catch (e) {
    history.value = []
  } finally {
    historyLoading.value = false
  }
}

function onClose() { emit('close') }
function onEscKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    emails.value = []
    openHistory.value = null
    load()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))

return { __sfc: true,props, emit, proxy, companyId, companyGroupId, baseUrl, loading, emails, openHistory, history, historyLoading, first, recipientName, recipientEmail, statusLabel, statusColor, eventLabel, formatTime, formatWhen, authConfig, load, toggleHistory, onClose, onEscKey, tagChipStyle, emailLabel, emailSourceLabel }
}

})